import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download, ExternalLink, Mail, Globe, BarChart2, Users, Calendar } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const PARTY_FACTS = [
  { id: "ALP", name: "Australian Labor Party", leader: "Anthony Albanese PM", founded: 1891, ideology: "Centre-left · Social democracy", color: "#CC2529" },
  { id: "LIB", name: "Liberal Party of Australia", leader: "Sussan Ley (Opposition Leader)", founded: 1944, ideology: "Centre-right · Liberal conservatism", color: "#1B4F9E" },
  { id: "GRN", name: "Australian Greens", leader: "Adam Bandt", founded: 1992, ideology: "Left · Green politics", color: "#10B981" },
  { id: "NAT", name: "The Nationals", leader: "David Littleproud", founded: 1920, ideology: "Centre-right · Agrarianism", color: "#155E3B" },
  { id: "TEA", name: "Teal Independents", leader: "Various (community independents)", founded: 2019, ideology: "Centre · Progressive conservatism", color: "#0D9488" },
  { id: "ON", name: "One Nation", leader: "Pauline Hanson", founded: 1997, ideology: "Right · Australian nationalism", color: "#D97706" },
];

const APP_STATS = [
  { label: "Political Parties Covered", value: "12+", icon: Users },
  { label: "Elections Tracked", value: "9", icon: Calendar },
  { label: "Voter Tools", value: "15+", icon: BarChart2 },
  { label: "States & Territories", value: "8", icon: Globe },
];

const USEFUL_LINKS = [
  { name: "Australian Electoral Commission (AEC)", url: "https://www.aec.gov.au", desc: "Official electoral authority" },
  { name: "ABC Election Coverage", url: "https://www.abc.net.au/news/politics", desc: "ABC News politics" },
  { name: "The Guardian Australia", url: "https://www.theguardian.com/australia-news", desc: "Australian news coverage" },
  { name: "Electoral Redistribution", url: "https://www.aec.gov.au/electoral-boundaries", desc: "Current electoral boundaries" },
];

export default function PressKitPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();

  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="press-kit-page">
      {/* Hero */}
      <div className="py-12 px-4 border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        <div className="max-w-5xl mx-auto">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-sm font-bold mb-5 hover:opacity-75"
            style={{ color: "#FFC82E" }}
          >
            <ArrowLeft className="w-4 h-4" /> Home
          </button>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Press Kit
          </h1>
          <p className="text-sm" style={{ color: "#94A3B8" }}>
            Media resources, party facts, and information about AusVote for journalists and researchers.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10">
        {/* About AusVote */}
        <section className="mb-12">
          <h2 className="font-black text-2xl mb-2" style={{ fontFamily: "Outfit, sans-serif", color: text }}>
            About AusVote
          </h2>
          <div className="border-l-4 pl-4 mb-6" style={{ borderColor: "#FFC82E" }}>
            <p className="text-sm leading-relaxed" style={{ color: textSec }}>
              AusVote is Australia's most comprehensive voter education platform, covering federal and all
              state/territory elections. Designed for 18–35 year old Australians, it uses AI to deliver
              on-the-pulse political insights, party comparisons, and civic education tools in real time.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {APP_STATS.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.label}
                  className="border-2 border-black p-4 text-center"
                  style={{ backgroundColor: surface, boxShadow: "3px 3px 0 #000" }}
                >
                  <Icon className="w-5 h-5 mx-auto mb-1" style={{ color: "#FFC82E" }} />
                  <p className="text-2xl font-black" style={{ fontFamily: "Outfit", color: text }}>{s.value}</p>
                  <p className="text-xs" style={{ color: textSec }}>{s.label}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Key Messaging */}
        <section className="mb-12">
          <h2 className="font-black text-2xl mb-4" style={{ fontFamily: "Outfit, sans-serif", color: text }}>
            Key Messaging
          </h2>
          <div className="space-y-3">
            {[
              "AusVote is non-partisan — we present all major parties fairly, without editorial bias.",
              "Our AI-powered tools (Fact Checker, Party Analysis, News Feed) help voters cut through political noise.",
              "We cover all 8 states and territories, tracking both federal and state elections with live countdowns.",
              "AusVote's Party Match Quiz has helped thousands of young Australians identify their political alignment.",
            ].map((msg, i) => (
              <div
                key={i}
                className="border-2 border-black p-3 flex items-start gap-3"
                style={{ backgroundColor: surface }}
              >
                <span
                  className="w-5 h-5 border-2 border-black flex items-center justify-center flex-shrink-0 text-xs font-black"
                  style={{ backgroundColor: "#FFC82E" }}
                >
                  {i + 1}
                </span>
                <p className="text-sm" style={{ color: text }}>{msg}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Party Quick Facts */}
        <section className="mb-12">
          <h2 className="font-black text-2xl mb-4" style={{ fontFamily: "Outfit, sans-serif", color: text }}>
            Party Quick Reference
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-2 border-black text-sm" style={{ backgroundColor: surface }}>
              <thead>
                <tr style={{ backgroundColor: "#0F172A" }}>
                  <th className="text-left px-4 py-2 font-black text-white border-b-2 border-black">Party</th>
                  <th className="text-left px-4 py-2 font-black text-white border-b-2 border-black">Leader</th>
                  <th className="text-left px-4 py-2 font-black text-white border-b-2 border-black">Founded</th>
                  <th className="text-left px-4 py-2 font-black text-white border-b-2 border-black">Ideology</th>
                </tr>
              </thead>
              <tbody>
                {PARTY_FACTS.map((p, i) => (
                  <tr
                    key={p.id}
                    className="border-b border-black"
                    style={{ backgroundColor: i % 2 === 0 ? "transparent" : isDark ? "#0F172A" : "#F9F7F2" }}
                  >
                    <td className="px-4 py-2.5">
                      <span
                        className="inline-block px-2 py-0.5 text-xs font-black border border-black mr-2"
                        style={{ backgroundColor: p.color, color: "#fff" }}
                      >
                        {p.id}
                      </span>
                      <span className="font-semibold" style={{ color: text }}>{p.name}</span>
                    </td>
                    <td className="px-4 py-2.5 text-xs" style={{ color: textSec }}>{p.leader}</td>
                    <td className="px-4 py-2.5 text-xs font-bold" style={{ color: text }}>{p.founded}</td>
                    <td className="px-4 py-2.5 text-xs" style={{ color: textSec }}>{p.ideology}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Useful Links */}
          <section>
            <h2 className="font-black text-2xl mb-4" style={{ fontFamily: "Outfit, sans-serif", color: text }}>
              Useful Links
            </h2>
            <div className="space-y-2">
              {USEFUL_LINKS.map((link) => (
                <a
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between border-2 border-black px-4 py-3 hover:-translate-y-0.5 transition-transform"
                  style={{ backgroundColor: surface, boxShadow: "2px 2px 0 #000" }}
                >
                  <div>
                    <p className="font-bold text-sm" style={{ color: text }}>{link.name}</p>
                    <p className="text-xs" style={{ color: textSec }}>{link.desc}</p>
                  </div>
                  <ExternalLink className="w-4 h-4 flex-shrink-0" style={{ color: "#FFC82E" }} />
                </a>
              ))}
            </div>
          </section>

          {/* Media Contact */}
          <section>
            <h2 className="font-black text-2xl mb-4" style={{ fontFamily: "Outfit, sans-serif", color: text }}>
              Media Contact
            </h2>
            <div
              className="border-2 border-black p-6"
              style={{ backgroundColor: "#0F172A", boxShadow: "4px 4px 0 #FFC82E" }}
            >
              <p className="font-black text-white text-lg mb-1" style={{ fontFamily: "Outfit" }}>
                AusVote Media Team
              </p>
              <p className="text-sm mb-4" style={{ color: "#94A3B8" }}>
                For media enquiries, interview requests, or republication of AusVote data:
              </p>
              <a
                href="mailto:media@ausvote.app"
                className="flex items-center gap-2 text-sm font-bold hover:opacity-75 transition-opacity"
                style={{ color: "#FFC82E" }}
              >
                <Mail className="w-4 h-4" /> media@ausvote.app
              </a>
              <a
                href="https://ausvote.app"
                className="flex items-center gap-2 text-sm font-bold mt-2 hover:opacity-75 transition-opacity"
                style={{ color: "#94A3B8" }}
              >
                <Globe className="w-4 h-4" /> ausvote.app
              </a>
            </div>

            <div
              className="border-2 border-black p-4 mt-4"
              style={{ backgroundColor: "#FFC82E25", borderColor: "#FFC82E" }}
            >
              <p className="text-xs font-bold mb-1" style={{ color: text }}>Usage Rights</p>
              <p className="text-xs" style={{ color: textSec }}>
                AusVote content is available for free use in educational and news contexts with attribution.
                Party data is compiled from publicly available AEC and party sources.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
